import i18next from 'i18next'
import * as Yup from 'yup'

const today = new Date()
today.setHours(0, 0, 0, 0)

export const createValidationSchema = Yup.object().shape({
  type: Yup.string().required(),
  scope: Yup.string().when('groupListing', {
    is: '1',
    then: Yup.string(),
    otherwise: Yup.string().required()
  }),
  full_address: Yup.mixed().test('full-address-required', 'Required', value =>
    !value?.isOptional ? !!value?.text : true
  ),
  suite: Yup.string().when('type', {
    is: 'condo',
    then: Yup.string().required(),
    otherwise: Yup.string()
  }),
  address: Yup.string().required(),
  building_name: Yup.string().when('type', {
    is: 'condo',
    then: Yup.string().required(),
    otherwise: Yup.string()
  }),
  city: Yup.string().required(),
  province: Yup.string().required(),
  zip: Yup.string()
    .nullable()
    .required('Required')
    .transform(value => value.replace(/\s+/g, ''))
    .when('country', {
      is: 'us',
      then: schema => schema.matches(/^\d{5}(-\d{4})?$/, i18next.t('Must be a valid zip code')),
      otherwise: schema =>
        schema
          .matches(/^[a-zA-Z0-9\s]*$/, i18next.t('Can only contain letters and numbers'))
          .test(
            'length',
            i18next.t('Must be a valid postal code'),
            value => value?.length === 3 || value?.length === 6
          )
          .matches(
            /^[a-zA-Z][0-9][a-zA-Z]([0-9][a-zA-Z][0-9])?$/i,
            i18next.t('Must be a valid postal code')
          )
    }),
  country: Yup.string().required()
})

export const detailsValidationSchema = Yup.object().shape({
  count_bedrooms: Yup.mixed().when('unit_type_scope_txt_id', {
    is: 'single',
    then: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .nullable(),
    otherwise: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(0)
      .max(99)
      .required()
  }),
  count_full_bathrooms: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(0)
    .max(99.5)
    .required(),
  count_dens: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(0)
    .max(99)
    .nullable(),
  max_occupancy: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(1)
    .nullable(),
  count_full_shared_bathrooms: Yup.mixed().when('unit_type_scope_txt_id', {
    is: 'entire',
    then: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .nullable(),
    otherwise: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(0)
      .max(99.5)
      .required()
  }),
  size: Yup.mixed().when('building.state', {
    is: 'ON',
    then: Yup.string().notRequired(),
    otherwise: Yup.mixed().when('isGroupListing', {
      is: false,
      then: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .min(1)
        .required(),
      otherwise: Yup.string().notRequired()
    })
  }),
  private_entrance: Yup.bool(),
  gr_min_size: Yup.mixed().when('isGroupListing', {
    is: true,
    then: Yup.number().positive().required(),
    otherwise: Yup.string().notRequired()
  }),
  gr_max_size: Yup.mixed().when('isGroupListing', {
    is: true,
    then: Yup.number().positive().required(),
    otherwise: Yup.string().notRequired()
  }),
  gr_count: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .nullable(),
  gr_floors: Yup.string(),
  furnished: Yup.string(),
  description: Yup.string(),
  allow_smoking: Yup.bool(),
  allow_pets: Yup.bool(),
  allow_cats: Yup.number().min(0).max(99).nullable(),
  allow_dogs: Yup.number().min(0).max(99).nullable(),
  price: Yup.mixed().when('isGroupListing', {
    is: false,
    then: Yup.number().positive().required(),
    otherwise: Yup.string().notRequired()
  }),
  gr_min_price: Yup.mixed().when('isGroupListing', {
    is: true,
    then: Yup.number().positive().required(),
    otherwise: Yup.string().notRequired()
  }),
  gr_max_price: Yup.mixed().when('isGroupListing', {
    is: true,
    then: Yup.number().positive().required(),
    otherwise: Yup.string().notRequired()
  }),
  price_frequency: Yup.string().required(),
  lease_type: Yup.string().required(),
  availability_date: Yup.date()
    .required()
    .min(today, i18next.t('Availability date cannot be in the past')),
  lease_length: Yup.string(),
  min_lease_period: Yup.mixed().when('lease_length', {
    is: 'other',
    then: Yup.number().positive().required(),
    otherwise: Yup.string()
  }),
  min_lease_period_type: Yup.string().when('lease_length', {
    is: 'other',
    then: Yup.string().required(),
    otherwise: Yup.string()
  }),
  parking: Yup.object().shape({
    enabled: Yup.bool(),
    hasFee: Yup.bool(),
    fee_frequency_txt_id: Yup.mixed().when('hasFee', {
      is: true,
      then: Yup.string()
        .test(
          'fee_frequency_txt_id',
          i18next.t('l.listing.edit.required.error', 'Required'),
          val => !!val && val !== 'free' && val !== 'not_available'
        )
        .required(),
      otherwise: Yup.string()
    }),
    fee: Yup.mixed().when('hasFee', {
      is: true,
      then: Yup.number()
        .moreThan(0, i18next.t('l.listing.edit.no_zero_fee.error', 'Fee must be greater than $0'))
        .required(),
      otherwise: Yup.string()
    })
  }),
  storage: Yup.object().shape({
    enabled: Yup.bool(),
    hasFee: Yup.bool(),
    fee_frequency_txt_id: Yup.mixed().when('hasFee', {
      is: true,
      then: Yup.string()
        .test(
          'fee_frequency_txt_id',
          i18next.t('l.listing.edit.required.error', 'Required'),
          val => !!val && val !== 'free' && val !== 'not_available'
        )
        .required(),
      otherwise: Yup.string()
    }),
    fee: Yup.mixed().when('hasFee', {
      is: true,
      then: Yup.number()
        .moreThan(0, i18next.t('l.listing.edit.no_zero_fee.error', 'Fee must be greater than $0'))
        .required(),
      otherwise: Yup.string()
    })
  }),
  isBoostYourListing: Yup.boolean(),
  duration: Yup.string().when('isBoostYourListing', {
    is: true,
    then: Yup.string().required(
      i18next.t('Duration is required when sponsored listing is checked')
    ),
    otherwise: Yup.string().notRequired()
  })
})

export const mediaValidationSchema = Yup.object().shape({
  images: Yup.array().min(
    1,
    i18next.t('l.listing.edit.no_image_selected.error', 'Please upload at least 1 photo')
  )
})

export const editValidationSchema = detailsValidationSchema.concat(mediaValidationSchema).shape({
  unit_number: Yup.string().when(['unit_type_txt_id', 'isGroupListing'], {
    is: (unit_type_txt_id: string, isGroupListing: boolean) =>
      unit_type_txt_id === 'condo' || isGroupListing === true,
    then: Yup.string().required(),
    otherwise: Yup.string()
  })
})
